/**
 * @fileoverview Hook for ecommerce totals and trends from dashboard data.
 * @module shared/hooks/dashboard/useEcommerceTotals
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardData } from './useDashboardData'
import { formatMoneyCompact } from '@/shared/utils/money'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { MetricCardData } from '@/shared/types/metrics'
import { useFilter } from '@/shared/hooks/useFilter'

/**
 * Ecommerce totals data structure.
 */
export interface EcommerceTotals {
  /** Total revenue */
  revenue: number
  /** Total costs */
  costs: number
  /** Total profit */
  profit: number
  /** Total orders */
  orders: number
  /** Revenue trend percentage */
  revenueTrend: number
  /** Cost trend percentage */
  costTrend: number
  /** Total ad spend (raw) */
  adSpend: number
  /** Total COGS (raw) */
  cogs: number
  /** Profit margin percentage (respects CM2/OC mode) */
  margin: number
}

/**
 * Hook result for ecommerce totals.
 */
export interface UseEcommerceTotalsResult {
  /** Totals data */
  data: EcommerceTotals | null
  /** Revenue metric card data */
  revenueCard: MetricCardData | null
  /** Cost metric card data */
  costCard: MetricCardData | null
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get ecommerce totals and trends from dashboard data.
 *
 * @returns Ecommerce totals with loading and error states
 * @example
 * ```typescript
 * const { data, revenueCard, costCard, isLoading } = useEcommerceTotals();
 * ```
 */
export function useEcommerceTotals(): UseEcommerceTotalsResult {
  const { t, i18n } = useTranslation('features-home')
  const { salesTimeQuery } = useDashboardData()
  const { currency: reportingCurrency } = useReportingCurrency()
  const { profitBasis } = useFilter()

  const result = useMemo(() => {
    const dataPoints = salesTimeQuery.data?.data || []

    if (dataPoints.length === 0) {
      return {
        data: null,
        revenueCard: null,
        costCard: null,
      }
    }

    // Calculate totals directly from unified structure
    const totals = dataPoints.reduce(
      (acc, point) => {
        const revenue = point.metrics.sales || 0
        const adSpend = point.metrics.advertising || 0
        const cogs = point.metrics.cogs || 0
        const direct = point.metrics.directCosts || 0
        const cm2 =
          typeof point.metrics.cm2 === 'number'
            ? point.metrics.cm2
            : revenue - (adSpend + cogs + direct)

        acc.totalRevenue += revenue
        acc.totalCosts += adSpend + cogs + direct
        acc.totalAdSpend += adSpend
        acc.totalCogs += cogs
        acc.netProfit += cm2
        acc.totalOrders += point.metrics.orderCount || 0
        return acc
      },
      {
        totalRevenue: 0,
        totalCosts: 0,
        totalAdSpend: 0,
        totalCogs: 0,
        netProfit: 0,
        totalOrders: 0,
      }
    )

    // Prefer the authoritative distinct-order count from the backend metadata.
    // Summing per-row orderCount inflates the total when the query groups by
    // PRODUCT/VARIANT (one order with N products fans across N rows).
    const totalOrderCountFromMeta = salesTimeQuery.data?.metadata?.totalOrderCount
    if (typeof totalOrderCountFromMeta === 'number') {
      totals.totalOrders = totalOrderCountFromMeta
    }

    // Compute Full Profit total if requested
    const allocatedIndirectTotal =
      salesTimeQuery.data?.metadata?.totalAllocatedIndirectCosts ??
      salesTimeQuery.data?.indirectCosts?.total ??
      0
    const ocTotalFromMeta = salesTimeQuery.data?.metadata?.totalAllocatedOperatingContribution
    const ocTotal =
      profitBasis === 'OC'
        ? typeof ocTotalFromMeta === 'number'
          ? ocTotalFromMeta
          : totals.netProfit - allocatedIndirectTotal
        : totals.netProfit

    // Calculate margin (uses OC when OC mode active)
    const margin = totals.totalRevenue > 0 ? (ocTotal / totals.totalRevenue) * 100 : 0

    // Calculate trends by comparing recent periods
    let revenueTrend = 0
    let costTrend = 0

    if (dataPoints.length >= 2) {
      const lastPeriod = dataPoints[dataPoints.length - 1]
      const secondLastPeriod = dataPoints[dataPoints.length - 2]

      if (lastPeriod && secondLastPeriod) {
        const recentRevenue = lastPeriod.metrics.sales || 0
        const olderRevenue = secondLastPeriod.metrics.sales || 0
        const recentCosts =
          (lastPeriod.metrics.advertising || 0) +
          (lastPeriod.metrics.cogs || 0) +
          (lastPeriod.metrics.directCosts || 0)
        const olderCosts =
          (secondLastPeriod.metrics.advertising || 0) +
          (secondLastPeriod.metrics.cogs || 0) +
          (secondLastPeriod.metrics.directCosts || 0)

        revenueTrend = olderRevenue > 0 ? ((recentRevenue - olderRevenue) / olderRevenue) * 100 : 0
        costTrend = olderCosts > 0 ? ((recentCosts - olderCosts) / olderCosts) * 100 : 0
      }
    }

    const data: EcommerceTotals = {
      revenue: totals.totalRevenue,
      costs: totals.totalCosts,
      profit: ocTotal,
      orders: totals.totalOrders,
      revenueTrend,
      costTrend,
      adSpend: totals.totalAdSpend,
      cogs: totals.totalCogs,
      margin,
    }

    const locale = i18n.language
    const compact = (amount: number, maximumFractionDigits = 1): string => {
      if (!reportingCurrency) {
        return `${Math.trunc(amount).toLocaleString(locale)}`
      }

      return formatMoneyCompact(
        amount,
        reportingCurrency,
        locale,
        maximumFractionDigits,
        'narrowSymbol'
      )
    }

    // Create metric cards directly
    const revenueCard: MetricCardData = {
      title: t('metricLabels.totalRevenue'),
      value: compact(totals.totalRevenue),
      trend: {
        value: Math.abs(revenueTrend),
        direction: revenueTrend >= 0 ? 'up' : 'down',
        period: t('metricLabels.vsPrevPeriod'),
      },
      secondaryMetrics: [
        {
          label: t('metricLabels.profit', { defaultValue: 'Profit' }),
          value: compact(ocTotal),
        },
        {
          label: t('metricLabels.margin'),
          value: `${margin.toFixed(1)}%`,
          trend: 'up',
        },
      ],
    }

    const costCard: MetricCardData = {
      title: t('metricLabels.totalCost'),
      value: compact(totals.totalCosts),
      trend: {
        value: Math.abs(costTrend),
        direction: costTrend >= 0 ? 'up' : 'down',
        period: t('metricLabels.vsPrevPeriod'),
      },
      secondaryMetrics: [
        {
          label: t('metricLabels.adSpend'),
          value: compact(totals.totalAdSpend),
        },
        {
          label: t('metricLabels.cogs'),
          value: compact(totals.totalCogs),
        },
      ],
    }

    return {
      data,
      revenueCard,
      costCard,
    }
  }, [salesTimeQuery.data, t, reportingCurrency, i18n.language, profitBasis])

  return {
    ...result,
    isLoading: salesTimeQuery.isLoading,
    isError: salesTimeQuery.isError,
    error: salesTimeQuery.error?.message || null,
  }
}
